'use client'

import { useSearchParams } from 'next/navigation'
import { createContext, PropsWithChildren, use, useContext } from 'react'

import { SEARCH_PARAM_KEYS } from '@/constants/auth'
import { Comment, CommentsData } from '@/types/comments'
import { IframePolicy, InitialMetaData, SceneMetaGroup, WorldMetaData } from '@/types/reticulum'

type WorldDetailsContextType = {
  worldMeta: WorldMetaData
  standaloneUrl: string
  iframeSettings: IframePolicy[]
  is3DCommerce: boolean
  comments: Comment[] | undefined
  commentsCount: number | undefined
}

const WorldDetailsContext = createContext<WorldDetailsContextType | null>(null)

const getFirstSceneId = (sceneUrls: string[]): string => {
  const sceneIdList = sceneUrls.map((url) => url.match(/\/(\d+)\.json$/)?.[1]).filter(Boolean)
  return sceneIdList[0] || ''
}

const convertWorldMetaData = (initWorldMeta: InitialMetaData): WorldMetaData => {
  const isSceneGroup = initWorldMeta.type === 'pc_scene_group'

  return {
    scene_type: initWorldMeta.type,
    scene_sid: isSceneGroup
      ? getFirstSceneId((initWorldMeta.data.meta as SceneMetaGroup).scene_urls)
      : initWorldMeta.data.sid,
    scene_settings: initWorldMeta.data.settings,
    scene_meta: initWorldMeta.data.meta,
    version: initWorldMeta.data.version,
    rtc_host: initWorldMeta.rtc_host,
    policy: initWorldMeta.policy,
    owner: initWorldMeta.owner,
    channel_name: initWorldMeta.channel_name,
    ticket_id: initWorldMeta.ticket_id,
    iframe_settings: initWorldMeta.data.iframe_settings,
  } as WorldMetaData
}

type WorldDetailsProviderProps = PropsWithChildren<{
  worldMetaPromise: Promise<InitialMetaData>
  commentsPromise: Promise<CommentsData> | null
  commentsCountPromise: Promise<CommentsData> | null
}>

export function WorldDetailsProvider({
  children,
  worldMetaPromise,
  commentsPromise,
  commentsCountPromise,
}: WorldDetailsProviderProps) {
  const initWorldMeta = use(worldMetaPromise)
  const worldMeta = convertWorldMetaData(initWorldMeta)
  const comments = commentsPromise ? use(commentsPromise)?.results : undefined
  const commentsCount = commentsCountPromise
    ? (use(commentsCountPromise)?.pages?.total ?? 0)
    : undefined

  const searchParams = useSearchParams()
  const previewMode = searchParams.get(SEARCH_PARAM_KEYS.PREVIEW) !== null

  const sceneType = worldMeta?.scene_type
  const standaloneUrl =
    sceneType === 'standalone'
      ? previewMode
        ? worldMeta?.scene_meta?.preview_url
        : 'scene_url' in worldMeta?.scene_meta
          ? worldMeta.scene_meta.scene_url
          : ''
      : ''
  const iframeSettings =
    sceneType === 'standalone'
      ? previewMode
        ? worldMeta?.iframe_settings?.preview
        : worldMeta?.iframe_settings?.publish
      : []

  const is3DCommerce =
    worldMeta?.scene_settings?.experimentalFlags?.includes('3DEC_short') ||
    worldMeta?.scene_settings?.eCommerceConfigs?.is3DEC

  return (
    <WorldDetailsContext.Provider
      value={{
        worldMeta,
        standaloneUrl,
        iframeSettings,
        is3DCommerce,
        comments,
        commentsCount,
      }}
    >
      {children}
    </WorldDetailsContext.Provider>
  )
}

export const useWorldDetailsContext = () => {
  const value = useContext(WorldDetailsContext)

  if (value == null) {
    throw new Error('useWorldDetailsContext cannot be used outside of WorldDetailsProvider')
  }

  return value
}
